import { Badge, Box, Heading, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import {
  FiBarChart2, FiCalendar, FiFileText, FiFlag, FiMap, FiShield, FiTag, FiTrendingUp, FiUsers,
} from 'react-icons/fi'
import type { IconType } from 'react-icons'
import { PageContainer } from '../components/shared/PageContainer'
import { useReportDefinitions } from '../hooks/useReports'
import type { ReportDefinition } from '../types/reporting'

const ICONS: Record<string, IconType> = {
  flag: FiFlag, calendar: FiCalendar, trendingUp: FiTrendingUp, users: FiUsers,
  fileText: FiFileText, map: FiMap, shield: FiShield, tag: FiTag,
}

function ReportCard({ definition }: { definition: ReportDefinition }) {
  const Icon = ICONS[definition.iconKey] ?? FiBarChart2
  const planned = definition.status === 'Planned'

  return (
    <Box
      bg="surface"
      p={6}
      borderRadius="lg"
      shadow="md"
      opacity={planned ? 0.6 : 1}
      _hover={planned ? undefined : { shadow: 'lg' }}
    >
      <VStack align="stretch" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Icon aria-hidden />
          <Heading size="sm">{definition.title}</Heading>
          {planned && <Badge colorPalette="gray" ml="auto">Planned</Badge>}
        </Box>
        <Text color="gray.600" fontSize="sm">{definition.question}</Text>
        {!planned && (
          <RouterLink to={`/dashboard/reports/${definition.key}`} aria-label={`Run report: ${definition.title}`}>
            <Text color="accent" fontWeight="medium" fontSize="sm">Run report →</Text>
          </RouterLink>
        )}
      </VStack>
    </Box>
  )
}

export function ReportCatalogPage() {
  const { data, isLoading, error } = useReportDefinitions()

  if (isLoading) {
    return (
      <PageContainer title="Reports">
        <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
      </PageContainer>
    )
  }
  if (error) {
    return (
      <PageContainer title="Reports">
        <Text color="red.600">Could not load the report catalogue.</Text>
      </PageContainer>
    )
  }

  const sections = Array.from(new Set((data ?? []).map((d) => d.section)))

  return (
    <PageContainer title="Reports" subtitle="Snapshots you can print, share, and export">
      <VStack align="stretch" gap={8}>
        {sections.map((section) => (
          <Box key={section}>
            <Heading size="md" mb={3}>{section}</Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
              {(data ?? []).filter((d) => d.section === section).map((d) => (
                <ReportCard key={d.key} definition={d} />
              ))}
            </SimpleGrid>
          </Box>
        ))}
      </VStack>
    </PageContainer>
  )
}
